var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { appFlowLiveUpdates, noUpdates, watchAppVersion } from "@common/AppUpdateNotification/strategies";
import { AppUpdateProvider, useAppUpdate } from "@common/AppUpdateNotification/AppUpdateContext";
import lg from "xAppLib/libs/logger";
import { useSiteStatus, useUser } from "@iscr/xAppLib/Hooks";
const logger = {
    debug: (context, message, data) => {
        if (app.settings.dev_env) {
            console.log(context, message, data);
        }
    },
    info: (context, mesage, data) => {
        // @ts-ignore
        lg.usg_log(context, "action", mesage, data);
    },
    error: (_context, error, data) => lg.report_except(error, data),
};
function parseSiteStatusValue(valueMaybe) {
    if (typeof valueMaybe === "boolean") {
        return valueMaybe;
    }
    if (typeof valueMaybe === "string") {
        switch (valueMaybe) {
            case "true":
                return true;
            case "false":
                return false;
            case "ia":
            case "wa":
                return valueMaybe;
            default:
                // We don't know what this value is, play it safe and default to off
                return false;
        }
    }
    // Nothing specified, go with our ideal/future-state of being enabled
    return true;
}
const POLL_DELAY_MS = 60 * 60 * 1000; /* one hour */
const fakeUpdate = notify => {
    const method = "__fakeUpdate";
    const fakeNotification = () => notify(new Date().toISOString());
    window[method] = fakeNotification;
    fakeNotification(); // initialise the current fake version
    console.log('CALL %s to trigger an "update"', method);
    return () => {
        delete window[method];
    };
};
const pollBuildInfo = notify => {
    const signal = new AbortController();
    (() => __awaiter(void 0, void 0, void 0, function* () {
        while (!signal.signal.aborted) {
            try {
                const rsp = yield fetch("/build.json");
                const info = yield rsp.json();
                if (info && typeof info === "object" && "vr" in info && typeof info.vr === "string") {
                    notify(info.vr);
                }
            }
            catch (error) {
                if (!signal.signal.aborted) {
                    logger.error("pollBuildInfo", error);
                }
            }
            yield new Promise(resolve => setTimeout(resolve, POLL_DELAY_MS));
        }
    }))();
    return () => {
        signal.abort();
    };
};
export function AppUpdateNudgeContext({ children }) {
    var _a, _b, _c;
    const user = useUser();
    const user_opt_in = (_b = (_a = user.prof) === null || _a === void 0 ? void 0 : _a.xtra) === null || _b === void 0 ? void 0 : _b.nudge;
    const feature_flag = (_c = useSiteStatus()) === null || _c === void 0 ? void 0 : _c.app_nudge;
    const app_nudge = parseSiteStatusValue(typeof user_opt_in === "undefined" ? feature_flag : user_opt_in);
    const strategy = useMemo(() => {
        if (app_nudge === false || (app_nudge === "ia" && !__IONIC__) || (app_nudge === "wa" && __IONIC__)) {
            return noUpdates();
        }
        if (__IONIC__) {
            return appFlowLiveUpdates({ logger });
        }
        return watchAppVersion({
            // subscribe: notify => site_model.watch_record("ver_app_nudge", notify),
            subscribe: app.settings.is_local ? fakeUpdate : pollBuildInfo,
        });
    }, [app_nudge]);
    const timing = app.settings.is_local ? { dismiss_ms: 60 * 1000 } : {};
    return (_jsx(AppUpdateProvider, Object.assign({ updater: strategy, logger: logger }, timing, { children: children })));
}
export function AppUpdateNudgeBanner() {
    const update = useAppUpdate();
    if (!update.shouldPrompt) {
        return null;
    }
    return (_jsx("div", { className: "sticky top-0 left-0 right-0 bg-amber-100 z-50 pt-safe pb-2 px-4 flex flex-row justify-center", children: _jsxs("span", { className: "flex flex-row justify-between w-full md:max-w-xl mt-2", children: [_jsx("span", { children: "App update available" }), _jsxs("span", { className: "space-x-4", children: [_jsx("a", { href: "#", onClick: e => {
                                e.preventDefault();
                                update.reload();
                            }, className: "font-bold", children: "Update now" }), _jsx("a", { href: "#", onClick: e => {
                                e.preventDefault();
                                update.dismiss();
                            }, children: "Later" })] })] }) }));
}
